import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import {
  Container, Typography, Box, Grid, CircularProgress, Chip
} from '@mui/material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CelebrationIcon from '@mui/icons-material/Celebration';
import GroupsIcon from '@mui/icons-material/Groups';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const GOLD = '#d4af37';

interface AnnadanamContent {
  title: string;
  subtitle: string;
  festivalOnlyNote: string;
  mainHeading: string;
  mainDescription: string;
  significance1Title: string;
  significance1Description: string;
  significance2Title: string;
  significance2Description: string;
  significance3Title: string;
  significance3Description: string;
  festivalMealsTitle: string;
  festivalMealsDescription: string;
  mealsPerDay: string;
  mealsCount: string;
  averageCost: string;
  festivalDays: string;
  donationLevel1Name: string;
  donationLevel1Amount: string;
  donationLevel1Description: string;
  donationLevel2Name: string;
  donationLevel2Amount: string;
  donationLevel2Description: string;
  donationLevel3Name: string;
  donationLevel3Amount: string;
  donationLevel3Description: string;
  improvementTitle: string;
  improvementNote: string;
  contactNote: string;
}

export default function Annadanam() {
  const [content, setContent] = useState<AnnadanamContent | null>(null);
  const [loading, setLoading] = useState(true);

  const defaultContent: AnnadanamContent = {
    title: 'Annadanam',
    subtitle: 'Sacred Food Offering During Annual Ulsavam',
    festivalOnlyNote: 'Annadanam is offered only during the annual Ulsavam period. It is not a year-round daily program.',
    mainHeading: 'Annadanam - The Divine Gift of Nourishment',
    mainDescription: 'Annadanam is the sacred offering of meals to devotees during our yearly Ulsavam days. At our temple, this service is conducted during the festival week and prepared with devotion for devotees who participate in the celebrations and rituals.',
    significance1Title: 'Spiritual Significance',
    significance1Description: 'In Hindu tradition, offering food to devotees is considered "Annadanam Maha Danam" (the greatest of all donations). It directly nourishes both body and spirit during sacred celebrations.',
    significance2Title: 'Community Bond',
    significance2Description: 'Annadanam during Ulsav brings the entire community together, fostering unity and shared spiritual experience. It strengthens our bonds as a temple family.',
    significance3Title: 'Festival Essence',
    significance3Description: 'The festival atmosphere is incomplete without the sacred sharing of meals. Your support makes celebrations more meaningful for every devotee who visits.',
    festivalMealsTitle: 'Annual Ulsavam Annadanam Schedule',
    festivalMealsDescription: 'Annadanam is organized only during the annual Ulsavam days. Devotees are served as part of the sacred festival observances.',
    mealsPerDay: 'Annadanam is conducted during Ulsavam days only',
    mealsCount: 'Annual observance - once every year during Ulsavam',
    averageCost: 'Donations are used for festival meal preparation and service',
    festivalDays: 'Usual Ulsavam window: March 9 to March 15 (dates may vary slightly)',
    donationLevel1Name: 'Day Sponsor',
    donationLevel1Amount: '',
    donationLevel1Description: 'Sponsor Annadanam for one Ulsavam day and support meal service for devotees attending the festival.',
    donationLevel2Name: 'Festival Supporter',
    donationLevel2Amount: '',
    donationLevel2Description: 'Support the full Ulsavam-week Annadanam program and strengthen this sacred community offering.',
    donationLevel3Name: 'Festival Benefactor',
    donationLevel3Amount: '',
    donationLevel3Description: 'Become a principal contributor for annual Ulsavam Annadanam and help sustain complete festival meal service.',
    improvementTitle: 'Your Contribution Improves Our Service',
    improvementNote: 'Donations help us enhance food quality, accommodate dietary preferences, and improve service efficiency during the festival.',
    contactNote: 'Annadanam is conducted only during annual Ulsavam days. For exact dates and coordination each year, please contact the temple office.'
  };

  useEffect(() => {
    const docRef = doc(db, 'annadanam_content', 'main');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      try {
        if (docSnap.exists()) {
          const data = docSnap.data() as AnnadanamContent;
          setContent(data);
        } else {
          setContent(null);
        }
      } catch (error) {
        console.error('Error processing annadanam content:', error);
      } finally {
        setLoading(false);
      }
    }, (error) => {
      console.error('Error setting up real-time listener:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const d = content || defaultContent;

  const stats = [
    { value: 'Ulsavam-Time Service', label: 'Annadanam is arranged only during festival days.', gradient: 'linear-gradient(135deg, #ea580c, #c2410c)' },
    { value: 'Yearly Observance', label: 'This sacred offering is conducted as part of annual celebrations.', gradient: 'linear-gradient(135deg, #d97706, #b45309)' },
    { value: 'Devotee-Focused', label: 'Meals are served to devotees who attend the Ulsavam rituals.', gradient: 'linear-gradient(135deg, #dc2626, #b91c1c)' },
    { value: 'Temple Tradition', label: 'The program continues the long-standing Annadanam heritage.', gradient: 'linear-gradient(135deg, #7c3aed, #6d28d9)' },
  ];

  const sigCards = [
    { title: d.significance1Title, desc: d.significance1Description, Icon: VolunteerActivismIcon, gradient: 'linear-gradient(135deg, #ea580c, #c2410c)' },
    { title: d.significance2Title, desc: d.significance2Description, Icon: GroupsIcon, gradient: 'linear-gradient(135deg, #2563eb, #1d4ed8)' },
    { title: d.significance3Title, desc: d.significance3Description, Icon: CelebrationIcon, gradient: 'linear-gradient(135deg, #7c3aed, #6d28d9)' },
  ];

  const tiers = [
    { name: d.donationLevel1Name, desc: d.donationLevel1Description, popular: false, gradient: 'linear-gradient(135deg, #475569, #334155)' },
    { name: d.donationLevel2Name, desc: d.donationLevel2Description, popular: false, gradient: 'linear-gradient(135deg, #ea580c, #f97316)' },
    { name: d.donationLevel3Name, desc: d.donationLevel3Description, popular: false, gradient: 'linear-gradient(135deg, #78350f, #92400e)' },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: '#f97316' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Annadanam — Kottayil Temple | അന്നദാനം" description="Contribute to Annadanam (free food distribution) at Kottayil Temple (Kotta-Kovilakam Kshethram). Support the noble tradition of feeding devotees at Muchukunnu." path="/donate/annadanam" keywords="annadanam, Kottayil temple annadanam, free food temple, feeding devotees, അന്നദാനം, കൊട്ടയിൽ ക്ഷേത്രം അന്നദാനം, temple food donation Kerala" />
      <PageBanner title={d.title} />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* ══════ VIBRANT HERO ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', mb: 3.5,
              background: 'linear-gradient(135deg, #7c2d12 0%, #ea580c 50%, #f97316 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.07, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3.5, sm: 4 }, pb: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <RestaurantIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Chip label="Sacred Food Offering" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.7rem', mb: 1, height: 24, backdropFilter: 'blur(8px)' }} />
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.4rem', sm: '1.7rem' }, color: '#fff', lineHeight: 1.2 }}>
                      {d.mainHeading}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, maxWidth: 600 }}>
                  {d.mainDescription}
                </Typography>
              </Box>
            </Box>

            {/* ══════ FESTIVAL ALERT ══════ */}
            <Box sx={{
              borderRadius: '20px', overflow: 'hidden', mb: 3.5,
              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
              border: '1.5px solid rgba(245,158,11,0.25)',
              boxShadow: '0 4px 20px rgba(245,158,11,0.08)',
              display: 'flex', alignItems: 'center', gap: 2,
              px: { xs: 2.5, sm: 3 }, py: 2,
            }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: '14px',
                bgcolor: 'rgba(234,88,12,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <InfoOutlinedIcon sx={{ fontSize: 22, color: '#ea580c' }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#9a3412', lineHeight: 1.6 }}>
                  {d.festivalOnlyNote}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#c2410c', mt: 0.3 }}>
                  Check the temple website for upcoming Ulsav dates
                </Typography>
              </Box>
            </Box>

            {/* ══════ STATS GRID ══════ */}
            <Grid container spacing={2} sx={{ mb: 3.5 }}>
              {stats.map((s, idx) => (
                <Grid item xs={6} sm={3} key={idx}>
                  <Box sx={{
                    borderRadius: '20px', overflow: 'hidden',
                    transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 40px rgba(0,0,0,0.1)' },
                  }}>
                    <Box sx={{ background: s.gradient, px: 2, pt: 2.5, pb: 3.5, position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
                      <Box sx={{ position: 'absolute', inset: 0, opacity: 0.08, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '12px 12px' }} />
                      <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.2rem', sm: '1.5rem' }, color: '#fff', position: 'relative', zIndex: 2 }}>
                        {s.value}
                      </Typography>
                      <Box sx={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 14, bgcolor: '#fff', borderRadius: '50% 50% 0 0 / 100% 100% 0 0' }} />
                    </Box>
                    <Box sx={{ bgcolor: '#fff', textAlign: 'center', px: 1.5, pt: 0, pb: 1.5, border: '1px solid rgba(0,0,0,0.06)', borderTop: 0, borderRadius: '0 0 20px 20px' }}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#888', fontWeight: 600 }}>{s.label}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* ══════ SIGNIFICANCE CARDS ══════ */}
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
              {sigCards.map((card, idx) => (
                <Grid item xs={12} sm={4} key={idx}>
                  <Box sx={{
                    bgcolor: '#fff', borderRadius: '24px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 50px rgba(0,0,0,0.08)', '& .sigIcon': { transform: 'scale(1.15) rotate(8deg)' } },
                  }}>
                    <Box sx={{ background: card.gradient, px: 2.5, pt: 2.5, pb: 3.5, position: 'relative', overflow: 'hidden' }}>
                      <Box sx={{ position: 'absolute', inset: 0, opacity: 0.08, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '12px 12px' }} />
                      <Box className="sigIcon" sx={{
                        width: 48, height: 48, borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
                        border: '2px solid rgba(255,255,255,0.25)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        position: 'relative', zIndex: 2,
                        transition: 'transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      }}>
                        <card.Icon sx={{ fontSize: 22, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff', mt: 1.5, position: 'relative', zIndex: 2 }}>
                        {card.title}
                      </Typography>
                      <Box sx={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 16, bgcolor: '#fff', borderRadius: '50% 50% 0 0 / 100% 100% 0 0' }} />
                    </Box>
                    <Box sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                      <Typography sx={{ fontSize: '0.76rem', color: '#888', lineHeight: 1.7 }}>{card.desc}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* ══════ DONATION TIERS ══════ */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 3.5,
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #ea580c, #f97316)',
                px: { xs: 2.5, sm: 3 }, py: 2,
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <AutoAwesomeIcon sx={{ fontSize: 20, color: '#fff' }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>
                  Annadanam Support Options
                </Typography>
              </Box>

              <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
                <Grid container spacing={2.5}>
                  {tiers.map((tier, idx) => (
                    <Grid item xs={12} sm={4} key={idx}>
                      <Box sx={{
                        borderRadius: '20px', overflow: 'hidden', position: 'relative',
                        border: tier.popular ? '2px solid rgba(249,115,22,0.35)' : '1px solid rgba(0,0,0,0.06)',
                        transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 50px rgba(0,0,0,0.08)' },
                      }}>
                        <Box sx={{ background: tier.gradient, px: 2.5, pt: 2.5, pb: 3.5, position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
                          <Box sx={{ position: 'absolute', inset: 0, opacity: 0.08, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '12px 12px' }} />
                          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#fff', position: 'relative', zIndex: 2, mb: 0.5 }}>
                            {tier.name}
                          </Typography>
                          <Box sx={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 16, bgcolor: '#fff', borderRadius: '50% 50% 0 0 / 100% 100% 0 0' }} />
                        </Box>
                        <Box sx={{ px: 2, pt: 0, pb: 2 }}>
                          <Typography sx={{ fontSize: '0.74rem', color: '#888', lineHeight: 1.7, textAlign: 'center' }}>{tier.desc}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* ══════ IMPROVEMENT & CONTACT (Dark Slate Footer) ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', bottom: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(249,115,22,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 3.5 }, py: 3.5, position: 'relative', zIndex: 2 }}>
                {/* Improvement */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 3 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #e5c158)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.3,
                  }}>
                    <TipsAndUpdatesIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff', mb: 0.5 }}>{d.improvementTitle}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>{d.improvementNote}</Typography>
                  </Box>
                </Box>

                <Box sx={{ width: '100%', height: 1, bgcolor: 'rgba(255,255,255,0.06)', my: 2 }} />

                {/* Contact */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    bgcolor: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.3,
                  }}>
                    <PhoneInTalkIcon sx={{ fontSize: 20, color: '#fb923c' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff', mb: 0.5 }}>Festival Schedule & Contact</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>{d.contactNote}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="donate" activePath="/donate/annadanam" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
